import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { QrCodeDialog } from "../components/QrCodeDialog";
import { api, formatApiError, PLATFORM_META } from "../api";
import { PlatformLogo } from "../components/PlatformLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Save, Copy, Shield, Star, QrCode } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const PLATFORM_HELP = {
  tripadvisor: "https://www.tripadvisor.com/UserReviewEdit-…",
  google: "https://search.google.com/local/writereview?…",
  trustpilot: "https://www.trustpilot.com/evaluate/…",
  facebook: "https://www.facebook.com/{page}/reviews",
};

const CATEGORY_LABELS = {
  tour: {
    newTitle: "new_tour_title",
    editTitle: "edit_tour_title",
    nameLabel: "tour_name",
    namePh: "tour_name_ph",
    listRoute: "/dashboard/tours",
    backKey: "back_to_tours",
  },
  restaurant: {
    newTitle: "new_restaurant_title",
    editTitle: "edit_restaurant_title",
    nameLabel: "restaurant_name",
    namePh: "restaurant_name_ph",
    listRoute: "/dashboard/restaurants",
    backKey: "back_to_tours",
  },
  hotel: {
    newTitle: "new_hotel_title",
    editTitle: "edit_hotel_title",
    nameLabel: "hotel_name",
    namePh: "hotel_name_ph",
    listRoute: "/dashboard/hotels",
    backKey: "back_to_tours",
  },
  service: {
    newTitle: "new_service_title",
    editTitle: "edit_service_title",
    nameLabel: "service_name",
    namePh: "service_name_ph",
    listRoute: "/dashboard/services",
    backKey: "back_to_tours",
  },
};

export default function TourEditor({ category = "tour" }) {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { t } = useLang();
  const cfg = CATEGORY_LABELS[category] || CATEGORY_LABELS.tour;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    description: "",
    rating_threshold: 4,
    category,
    platforms: { tripadvisor: "", google: "", trustpilot: "", facebook: "" },
  });
  const [tourId, setTourId] = useState(null);
  const [feedback, setFeedback] = useState([]);

  useEffect(() => {
    if (isNew) {
      setForm((s) => ({ ...s, category }));
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/tours/${id}`);
        setForm({
          name: data.name,
          description: data.description || "",
          rating_threshold: data.rating_threshold ?? 4,
          category: data.category || category,
          platforms: {
            tripadvisor: data.platforms?.tripadvisor || "",
            google: data.platforms?.google || "",
            trustpilot: data.platforms?.trustpilot || "",
            facebook: data.platforms?.facebook || "",
          },
        });
        setTourId(data.id);
        api.get(`/tours/${id}/private-feedback`).then((r) => setFeedback(r.data)).catch(() => {});
      } catch (e) {
        toast.error(formatApiError(e));
        navigate(cfg.listRoute);
      } finally { setLoading(false); }
    })();
  }, [id, isNew, navigate, category]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("name_required")); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/tours", form);
        toast.success(t("toast_tour_created"));
        navigate(`${cfg.listRoute}/${data.id}`);
      } else {
        await api.put(`/tours/${id}`, form);
        toast.success(t("toast_saved"));
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const setPlatform = (k, v) => setForm((s) => ({ ...s, platforms: { ...s.platforms, [k]: v } }));

  const publicLink = tourId ? `${window.location.origin}/r/${tourId}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast.success(t("toast_link_copied"));
  };

  if (loading) return <DashboardLayout><div>{t("loading")}</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <button onClick={() => navigate(cfg.listRoute)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="back-to-tours">
        <ArrowLeft className="w-4 h-4" /> {t(cfg.backKey)}
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <h1 className="font-display text-3xl font-bold text-[#0A2540] tracking-tight">
          {isNew ? t(cfg.newTitle) : t(cfg.editTitle)}
        </h1>
        {!isNew && tourId && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyLink} data-testid="editor-copy-link-btn">
              <Copy className="w-4 h-4 mr-2" /> {t("copy_customer_link")}
            </Button>
            <QrCodeDialog
              url={publicLink}
              name={form.name}
              trigger={
                <Button variant="outline" data-testid="editor-qr-btn">
                  <QrCode className="w-4 h-4 mr-2" /> {t("qr_code_btn")}
                </Button>
              }
            />
          </div>
        )}
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6" data-testid="tour-form">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="space-y-5">
              <div>
                <Label className="text-sm">{t(cfg.nameLabel)}</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  data-testid="tour-name-input"
                  placeholder={t(cfg.namePh)}
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label className="text-sm">{t("tour_description")}</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                  data-testid="tour-description-input"
                  className="mt-1.5"
                  rows={3}
                  placeholder={t("tour_description_ph")}
                />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="font-display font-semibold text-lg text-[#0A2540] mb-1">{t("platforms_title")}</h2>
            <p className="text-sm text-slate-500 mb-6">{t("platforms_desc")}</p>
            <div className="space-y-5">
              {Object.entries(PLATFORM_META).map(([key, meta]) => (
                <div key={key}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <PlatformLogo platform={key} color={meta.color} size="w-7 h-7" iconSize="w-4 h-4" />
                    <Label className="text-sm">{meta.label}</Label>
                  </div>
                  <Input
                    type="url"
                    value={form.platforms[key]}
                    onChange={(e) => setPlatform(key, e.target.value)}
                    data-testid={`platform-${key}-input`}
                    placeholder={PLATFORM_HELP[key]}
                    className="h-11"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="smart-filter-section">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2B8C9F]/10 grid place-items-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#2B8C9F]" />
              </div>
              <div className="flex-1">
                <h2 className="font-display font-semibold text-lg text-[#0A2540]">{t("smart_filter_title")}</h2>
                <p className="text-sm text-slate-500 mt-1">{t("smart_filter_desc")}</p>
              </div>
            </div>
            <div className="mt-5">
              <Label className="text-sm">{t("smart_filter_label")}</Label>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="threshold-selector">
                {[1, 2, 3, 4, 5].map((n) => {
                  const active = form.rating_threshold === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, rating_threshold: n }))}
                      data-testid={`threshold-${n}`}
                      className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${active ? "bg-[#0A2540] text-white border-[#0A2540]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                    >
                      {t("smart_filter_option", { n })}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                {form.rating_threshold === 1 ? t("smart_filter_hint_all") : t("smart_filter_hint", { n: form.rating_threshold })}
              </p>
            </div>
          </div>

          {!isNew && (
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="private-feedback-list">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-semibold text-lg text-[#0A2540]">{t("feedback_title")}</h2>
                <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">{feedback.length}</span>
              </div>
              {feedback.length === 0 ? (
                <p className="text-sm text-slate-500">{t("feedback_empty")}</p>
              ) : (
                <ul className="space-y-4">
                  {feedback.map((f) => (
                    <li key={f.id} className="border border-slate-200 rounded-lg p-4" data-testid={`feedback-${f.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} className={`w-4 h-4 ${n <= f.rating ? "fill-[#E57C4F] text-[#E57C4F]" : "text-slate-300"}`} />
                          ))}
                        </div>
                        <span className="text-xs text-slate-500">{new Date(f.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-slate-800 whitespace-pre-wrap">{f.text}</p>
                      {(f.contact_name || f.contact_email) && (
                        <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex flex-wrap gap-3">
                          {f.contact_name && <span>👤 {f.contact_name}</span>}
                          {f.contact_email && (
                            <a href={`mailto:${f.contact_email}`} className="text-[#2B8C9F] hover:underline" data-testid={`reply-${f.id}`}>
                              ✉️ {f.contact_email}
                            </a>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-[#0A2540] rounded-xl p-6 text-white sticky top-6">
            <h3 className="font-display font-semibold text-lg">{t("how_it_works")}</h3>
            <ol className="text-sm mt-4 space-y-3 opacity-90">
              <li><span className="font-semibold">1.</span> {t("how_1")}</li>
              <li><span className="font-semibold">2.</span> {t("how_2")}</li>
              <li><span className="font-semibold">3.</span> {t("how_3")}</li>
              <li><span className="font-semibold">4.</span> {t("how_4")}</li>
            </ol>
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-tour-btn"
              className="w-full mt-6 rounded-md bg-[#E57C4F] hover:bg-[#E57C4F]/90 text-white h-11 gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? t("saving") : t("save_tour")}
            </Button>
          </div>
        </div>
      </form>
    </DashboardLayout>
  );
}
